// Browser-side ASIS frame codec. Mirrors src/protocol/frame.ts (Node/Buffer)
// but works in AudioWorklet + main thread without importing Buffer. The wire
// format is byte-identical: 12-byte header + Int16 LE PCM payload.
//
//   magic[0..1] = 0x41, 0x53 ("AS")
//   version     = 1
//   direction   = 1 (mic up) or 2 (speaker down)
//   epoch       = uint32 LE
//   seq         = uint32 LE
//   payload     = raw PCM Int16 LE samples
import { DIR_MIC, DIR_SPEAKER, FRAME_HEADER_BYTES, FRAME_MAGIC, FRAME_VERSION } from '../protocol/constants.js';

export type FrameDirection = 1 | 2;

export interface DecodedFrame {
  direction: FrameDirection;
  epoch: number;
  seq: number;
  samples: Int16Array;
}

export class WebFrameError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'WebFrameError';
    this.code = code;
  }
}

export interface EncodeInput {
  direction: FrameDirection;
  epoch: number;
  seq: number;
  samples: Int16Array;
}

export function encodeWebFrame(input: EncodeInput): Uint8Array {
  const { direction, epoch, seq, samples } = input;
  if (direction !== DIR_MIC && direction !== DIR_SPEAKER) {
    throw new WebFrameError(`bad direction ${direction}`, 'bad_direction');
  }
  if (samples.byteLength % 2 !== 0) {
    throw new WebFrameError('payload not aligned to int16', 'bad_alignment');
  }
  const out = new Uint8Array(FRAME_HEADER_BYTES + samples.byteLength);
  const view = new DataView(out.buffer);
  out[0] = FRAME_MAGIC[0];
  out[1] = FRAME_MAGIC[1];
  out[2] = FRAME_VERSION;
  out[3] = direction;
  view.setUint32(4, (epoch >>> 0) & 0xffffffff, true);
  view.setUint32(8, (seq >>> 0) & 0xffffffff, true);
  out.set(new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength), FRAME_HEADER_BYTES);
  return out;
}

export function tryDecodeWebFrame(buf: Uint8Array, maxFrameBytes: number): DecodedFrame | null {
  if (buf.length < FRAME_HEADER_BYTES) return null;
  if (buf.length > maxFrameBytes) {
    throw new WebFrameError(`frame too large: ${buf.length} > ${maxFrameBytes}`, 'frame_too_large');
  }
  if (buf[0] !== FRAME_MAGIC[0] || buf[1] !== FRAME_MAGIC[1]) {
    throw new WebFrameError('bad magic', 'bad_magic');
  }
  if (buf[2] !== FRAME_VERSION) {
    throw new WebFrameError(`unsupported version ${buf[2]}`, 'bad_version');
  }
  const direction = buf[3] as FrameDirection;
  if (direction !== DIR_MIC && direction !== DIR_SPEAKER) {
    throw new WebFrameError(`bad direction ${direction}`, 'bad_direction');
  }
  if ((buf.length - FRAME_HEADER_BYTES) % 2 !== 0) {
    throw new WebFrameError('payload not aligned to int16', 'bad_alignment');
  }
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const epoch = view.getUint32(4, true);
  const seq = view.getUint32(8, true);
  const samples = new Int16Array(buf.buffer, buf.byteOffset + FRAME_HEADER_BYTES, (buf.length - FRAME_HEADER_BYTES) / 2);
  return { direction, epoch, seq, samples };
}

export function samplesToBase64Web(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

export function base64ToSamplesWeb(b64: string): Int16Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  if (bytes.byteLength % 2 !== 0) {
    throw new WebFrameError('base64 payload not aligned', 'bad_alignment');
  }
  return new Int16Array(bytes.buffer, 0, bytes.byteLength / 2);
}
